import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import { FaBell, FaChevronLeft, FaInfoCircle, FaTrophy } from 'react-icons/fa';
import { formatDistanceToNow } from 'date-fns';
import '../scss/Notifications.scss';
import api from '../services/apiService';
import { achievements } from '../data/achievements';
import NotificationDetailModal from './NotificationDetailModal';

// Create a map of achievement IDs to their details for quick lookup
const achievementsMap = achievements.reduce((map, achievement) => {
  map[achievement.id] = achievement;
  return map;
}, {});

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const navigate = useNavigate();

  // Fetch notifications from API
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Get the team_id and player_id from sessionStorage
        const teamId = sessionStorage.getItem('selected_team_id');
        const playerId = sessionStorage.getItem('player_id');
        
        if (!teamId) {
          console.error('No team ID found in session storage');
          setNotifications([]);
          setIsLoading(false);
          return;
        }

        // Build the API URL with both team_id and player_id if available
        let apiUrl = `${process.env.REACT_APP_API_URL}/get_announcements.php?team_id=${teamId}`;
        if (playerId) {
          apiUrl += `&player_id=${playerId}`;
          console.log('Including player_id in notification request:', playerId);
        } else {
          console.log('No player_id found in session storage');
        }

        // Fetch announcements from API with parameters
        const response = await api.get(apiUrl);
        
        if (response.data.success && response.data.announcements) {
          // Log the response to check is_read values
          console.log('API Response - Announcements:', response.data.announcements);
          
          // Process and format the announcements
          const formattedNotifications = response.data.announcements.map((announcement, index) => {
            // Extract a title from the message if it has a colon format
            const message = announcement.message || '';
            const colonIndex = message.indexOf(':');
            
            // Determine the notification type (if returned from API)
            const type = announcement.type || 'announcement';
            
            // Determine if this is a player-specific notification
            const isPlayerSpecific = announcement.target_type === 'player';
            
            // Set title based on type
            let title;
            if (type === 'mention') {
              title = 'Match Report Mention';
            } else if (type === 'achievement') {
              title = 'Achievement Unlocked';
            } else if (type === 'last_match') {
              title = 'Match Update';
            } else if (type === 'update') {
              title = 'Team Update';
            } else if (type === 'response') {
              title = 'Availability Update';
            } else if (colonIndex > 0) {
              // For announcements, extract title from message content if it has a colon
              title = message.substring(0, colonIndex).trim();
            } else {
              // Default fallback
              title = type === 'announcement' ? 'Announcement' : 'Notification';
            }
            
            // Use the full message as content to ensure nothing is cut off
            const content = message;
            
            // Parse date from created_at if available, otherwise use current date
            let timestamp;
            if (announcement.created_at) {
              try {
                timestamp = new Date(announcement.created_at);
                // If date is invalid, fall back to current date
                if (isNaN(timestamp.getTime())) {
                  timestamp = new Date();
                }
              } catch (error) {
                timestamp = new Date();
              }
            } else {
              timestamp = new Date();
            }
            
            return {
              id: announcement.notification_id || index + 1,
              type: type,
              title: title,
              content: content,
              message: message, // Keep original message
              timestamp: timestamp,
              isRead: announcement.is_read === "1" || announcement.is_read === true,
              isPlayerSpecific: isPlayerSpecific,
              player_id: announcement.player_id,
              // Add actions if they can be determined from the message content
              actions: hasMatchInfo(message) ? [
                { label: 'View Match', action: 'viewMatch' }
              ] : []
            };
          });
          
          setNotifications(formattedNotifications);
          
          // Dispatch event to update notification badge in the header
          window.dispatchEvent(new Event('notificationUpdate'));
        } else {
          console.error('Failed to fetch announcements:', response.data);
          setNotifications([]);
        }
      } catch (error) {
        console.error('Error fetching announcements:', error);
        setNotifications([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter notifications when the active filter or notifications change
  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredNotifications(notifications);
    } else {
      setFilteredNotifications(
        notifications.filter(notification => {
          if (activeFilter === 'mentions') return notification.type === 'mention';
          if (activeFilter === 'achievements') return notification.type === 'achievement';
          if (activeFilter === 'updates') return notification.type === 'update' || notification.type === 'last_match';
          return true;
        })
      );
    }
  }, [activeFilter, notifications]);

  // Helper function to detect if a message contains match information
  const hasMatchInfo = (message) => {
    if (!message) return false;
    const lowerMessage = message.toLowerCase();
    return lowerMessage.includes('match') && 
           (lowerMessage.includes('pm') || lowerMessage.includes('am') || 
            lowerMessage.includes(':') || lowerMessage.includes('@'));
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
    
    // Mark as read on server
    markNotificationAsRead(notification.id);
    
    // Mark as read locally
    if (!notification.isRead) {
      console.log('Marking notification as read locally:', notification.id);
      
      const updatedNotifications = notifications.map(n => 
        n.id === notification.id ? { ...n, isRead: true } : n
      );
      setNotifications(updatedNotifications);
      
      // Also update filtered notifications
      setFilteredNotifications(prevFiltered => 
        prevFiltered.map(n => 
          n.id === notification.id ? { ...n, isRead: true } : n
        )
      );
    } else {
      console.log('Notification already marked as read:', notification.id);
    }
    
    // Show detail modal
    setSelectedNotification(notification);
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const formData = new FormData();
      formData.append('notification_id', notificationId);
      
      // Use the same base URL pattern as other API calls in the app
      let apiUrl = process.env.REACT_APP_API_URL;
      
      // Log the API endpoint for debugging
      console.log('Marking notification as read, API URL:', `${apiUrl}/mark_notification_read.php`);
      
      await api.post(`${apiUrl}/mark_notification_read.php`, formData);
      
      // Notify other components (like the Header) that a notification has been read
      window.dispatchEvent(new Event('notificationUpdate'));
      
      // No need to update UI here as we've already done it in handleNotificationClick
      console.log('Notification marked as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Continue showing the notification even if marking as read fails
    }
  };

  const closeModal = () => {
    setSelectedNotification(null);
  };

  const formatNotificationContent = (notification) => {
    const message = notification.message || '';
    
    // Handle different notification types
    switch (notification.type) {
      case 'mention':
        // Check if the message has a player's name at the start (typically contains 'has been mentioned')
        if (message.includes('has been mentioned')) {
          // Extract the player name from the beginning of the message
          const hasBeenIndex = message.indexOf('has been');
          if (hasBeenIndex > 0) {
            let playerName = message.substring(0, hasBeenIndex).trim();
            
            // Check if player name contains an achievement
            const dashIndex = playerName.indexOf(' - ');
            if (dashIndex > 0) {
              // Not using achievementName for now
              // const achievementName = playerName.substring(dashIndex + 3);
              playerName = playerName.substring(0, dashIndex);
            }
            
            // Extract the match description (between "match report" and the final colon)
            const matchReportIndex = message.indexOf('match report');
            const finalColonIndex = message.lastIndexOf(':');
            
            let matchDescription = '';
            let comment = '';
            
            if (matchReportIndex > 0 && finalColonIndex > matchReportIndex) {
              matchDescription = message.substring(matchReportIndex + 13, finalColonIndex).trim();
              comment = message.substring(finalColonIndex + 1).trim();
              
              return (
                <>
                  <div className="match-description">{matchDescription}</div>
                  <div className="mention-comment">{comment}</div>
                </>
              );
            }
            
            // Fallback to the old format if we can't parse the new format
            const colonIndex = message.indexOf(':');
            if (colonIndex > 0) {
              const comment = message.substring(colonIndex + 1).trim();
              return (
                <>
                  <div className="mention-comment">{comment}</div>
                </>
              );
            }
          }
        }
        break;
        
      case 'achievement':
        // Try to extract achievement ID from the message
        let achievementId = '';
        
        // First check if it's directly in the message (old format)
        if (message.includes('unlocked the')) {
          const startIdx = message.indexOf('unlocked the') + 12;
          const endIdx = message.indexOf('achievement', startIdx) - 1;
          if (endIdx > startIdx) {
            // Convert the extracted text to kebab-case to match achievement IDs
            achievementId = message.substring(startIdx, endIdx)
              .trim()
              .toLowerCase()
              .replace(/\s+/g, '-');
          }
        } else {
          // Check if the message itself is an achievement ID
          achievementId = message.trim();
        }
        
        // Look up achievement details
        const achievement = achievementsMap[achievementId];
        
        if (achievement) {
          return (
            <div className="achievement-notification">
              <div className="achievement-icon">
                {achievement.icon || <FaTrophy />}
              </div>
              <div className="achievement-content">
                <div className="achievement-title">{achievement.title}</div>
                <div className="achievement-description">{achievement.description}</div>
              </div>
            </div>
          );
        }
        
        // Fallback for unknown achievements
        return (
          <div className="achievement-notification">
            <div className="achievement-icon">🏆</div>
            <div className="achievement-text">{message}</div>
          </div>
        );
        
      case 'last_match':
        // Try to extract match score or result if present in the message
        const matchResult = message.match(/\b\d+\s*-\s*\d+\b/); // Look for patterns like "2-1"
        if (matchResult) {
          return (
            <div className="match-notification">
              <div className="match-result">{matchResult[0]}</div>
              <div className="match-details">{message}</div>
            </div>
          );
        }
        break;
        
      case 'update':
        return (
          <div className="update-notification">
            <div className="update-text">{message}</div>
          </div>
        );
        
      default:
        // Default return the content as is
        return notification.content;
    }
    
    // Default return the content as is
    return notification.content;
  };

  const renderNotification = (notification) => (
    <div 
      key={notification.id} 
      className={`notification-item ${!notification.isRead ? 'unread' : ''} ${notification.isPlayerSpecific ? 'personal' : ''}`}
      onClick={() => handleNotificationClick(notification)}
    >
      <div className="notification-icon">
        <FaBell />
      </div>
      <div className="notification-content">
        <div className="notification-header">
          <span className="title">{notification.title}</span>
          {notification.timestamp && (
            <span className="time">
              {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
            </span>
          )}
        </div>
        <div className="notification-body">
          {formatNotificationContent(notification)}
        </div>
        <div className="notification-actions">
          <button className="view-details-button">
            <FaInfoCircle /> {notification.isRead ? 'View Again' : 'View Details'}
          </button>
        </div>
      </div>
    </div>
  );

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  return (
    <div className="notifications-page">
      <Header />
      <div className="notifications-container">
        <div className="sidebar">
          <div className="notifications-header">
            <button className="back-button" onClick={handleBack}>
              <FaChevronLeft /> Back
            </button>
            <h1>Notifications</h1>
          </div>
        </div>

        <div className="main-content">
          <div className="notification-filters">
            <button 
              className={`filter-button ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => handleFilterChange('all')}
            >
              All
            </button>
            <button 
              className={`filter-button ${activeFilter === 'mentions' ? 'active' : ''}`}
              onClick={() => handleFilterChange('mentions')}
            >
              Mentions
            </button>
            <button 
              className={`filter-button ${activeFilter === 'achievements' ? 'active' : ''}`}
              onClick={() => handleFilterChange('achievements')}
            >
              Achievements
            </button>
            <button 
              className={`filter-button ${activeFilter === 'updates' ? 'active' : ''}`}
              onClick={() => handleFilterChange('updates')}
            >
              Updates
            </button>
          </div>
          
          <div className="content-area">
            {isLoading ? (
              <div className="loading">Loading...</div>
            ) : filteredNotifications.length > 0 ? (
              <div className="notifications-list">
                {filteredNotifications.map(renderNotification)}
              </div>
            ) : (
              <div className="no-content">
                <FaBell className="icon" />
                <p>No notifications{activeFilter !== 'all' ? ' of this type' : ''} yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedNotification && (
        <NotificationDetailModal
          notification={selectedNotification}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default Notifications;